import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import dayjs from "dayjs";
import {
  VISITOR_STORAGE_KEYS,
  clearVisitorData,
  loadVisitorData,
} from "./visitorStorage";

const API_BASE = process.env.REACT_APP_API_BASE || "";

const formatDateKey = (value) => dayjs(value).format("YYYY-MM-DD");
const weekdayLabel = (value) => dayjs(value).format("dddd");
const dayLabel = (value) => dayjs(value).format("DD MMM YYYY");

const getWeekStart = (value) => {
  const d = dayjs(value);
  const day = d.day();
  if (day === 0) {
    return d.add(1, "day").startOf("day");
  }
  if (day === 6) {
    return d.add(2, "day").startOf("day");
  }
  return d.subtract(day - 1, "day").startOf("day");
};

export default function EnquiryCalendar({ variant = "full", visitorMode = false }) {
  const isCompact = variant === "compact";
  const [weekStart, setWeekStart] = useState(() => getWeekStart(dayjs()));
  const [eventsByDate, setEventsByDate] = useState({});
  const [calendarDate, setCalendarDate] = useState(dayjs());
  const [missingLogs, setMissingLogs] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [showDebug, setShowDebug] = useState(false);
  const lastFocusRefreshRef = useRef(0);
  const today = dayjs();
  const loadLocalVisitLogs = useCallback(
    () => loadVisitorData(VISITOR_STORAGE_KEYS.visitLogs, []),
    []
  );

  const fetchJsonSafe = async (url) => {
    const res = await fetch(url);
    const text = await res.text();
    const contentType = res.headers.get("content-type") || "";
    if (!res.ok) {
      console.error("Request failed:", url, res.status);
      return [];
    }
    if (!contentType.includes("application/json")) {
      console.error("Non-JSON response:", url, text.slice(0, 120));
      return [];
    }
    try {
      return JSON.parse(text);
    } catch (error) {
      console.error("Invalid JSON:", url, error);
      return [];
    }
  };

  const loadEvents = useCallback(async (startDate, endDate) => {
    const start = startDate.format("YYYY-MM-DD");
    const end = endDate.format("YYYY-MM-DD");
    const startKey = start;
    const endKey = end;

    if (visitorMode) {
      const visits = loadLocalVisitLogs();
      const map = {};
      const addEvent = (date, event) => {
        if (!date) return;
        const parsed = dayjs(date);
        if (!parsed.isValid()) return;
        const key = parsed.format("YYYY-MM-DD");
        if (key < startKey || key > endKey) return;
        map[key] = map[key] || [];
        map[key].push(event);
      };

      visits.forEach((v) => {
        const personLabel = v.person_type || "Person";
        addEvent(v.visit_date, {
          type: "visit",
          title: `${personLabel}: ${v.person_name || ""}`,
          enquiryCaseNum: v.enquiry_case_num,
          note: v.notes || "",
          personType: v.person_type || "",
        });
        if (v.next_visit_date) {
          addEvent(v.next_visit_date, {
            type: "next-visit",
            title: `Next Visit (${personLabel}): ${v.person_name || ""}`,
            enquiryCaseNum: v.enquiry_case_num,
            personType: v.person_type || "",
          });
        }
      });

      setEventsByDate(map);
      setMissingLogs([]);
      setConflicts([]);
      return;
    }

    const [visits, summons, audit, conflictRows] = await Promise.all([
      fetchJsonSafe(`${API_BASE}/api/visit-logs?from=${start}&to=${end}`),
      fetchJsonSafe(`${API_BASE}/api/summons/schedule?from=${start}&to=${end}`),
      fetchJsonSafe(`${API_BASE}/api/calendar/audit?from=${start}&to=${end}`),
      fetchJsonSafe(`${API_BASE}/api/calendar/conflicts?from=${start}&to=${end}&maxPerDay=6`),
    ]);

    const map = {};
    const addEvent = (date, event) => {
      if (!date) return;
      const parsed = dayjs(date);
      if (!parsed.isValid()) return;
      const key = parsed.format("YYYY-MM-DD");
      if (key < startKey || key > endKey) return;
      map[key] = map[key] || [];
      map[key].push(event);
    };

    visits.forEach((v) => {
      const personLabel = v.person_type || "Person";
      addEvent(v.visit_date, {
        type: "visit",
        title: `${personLabel}: ${v.person_name || ""}`,
        enquiryCaseNum: v.enquiry_case_num,
        note: v.notes || "",
        personType: v.person_type || "",
      });
      if (v.next_visit_date) {
        addEvent(v.next_visit_date, {
          type: "next-visit",
          title: `Next Visit (${personLabel}): ${v.person_name || ""}`,
          enquiryCaseNum: v.enquiry_case_num,
          personType: v.person_type || "",
        });
      }
    });

    summons.forEach((s) => {
      const kind = s.eventKind || "appearance";
      const isDispatch = kind === "dispatch";
      addEvent(s.date, {
        type: isDispatch ? "summon-dispatch" : "summon",
        title: `${s.noticeType} ${isDispatch ? "Dispatch" : "Appearance"} - ${s.name || "Alleged"}`,
        enquiryCaseNum: s.enquiryCaseNum,
        role: s.roleOfAlleged,
      });
    });

    setEventsByDate(map);
    setMissingLogs(Array.isArray(audit) ? audit : []);
    setConflicts(Array.isArray(conflictRows) ? conflictRows : []);
  }, [loadLocalVisitLogs, visitorMode]);

  const refreshEvents = useCallback(() => {
    if (isCompact) {
      const start = calendarDate.startOf("month");
      const end = calendarDate.endOf("month");
      loadEvents(start, end);
      return;
    }
    const weekEnd = weekStart.add(4, "day");
    loadEvents(weekStart, weekEnd);
  }, [calendarDate, isCompact, loadEvents, weekStart]);

  useEffect(() => {
    refreshEvents();
  }, [refreshEvents]);

  useEffect(() => {
    const handler = () => refreshEvents();
    window.addEventListener("visit-log-created", handler);
    window.addEventListener("summon-updated", handler);
    window.addEventListener("enquiry-updated", handler);
    window.addEventListener("visitor-storage-updated", handler);
    return () => {
      window.removeEventListener("visit-log-created", handler);
      window.removeEventListener("summon-updated", handler);
      window.removeEventListener("enquiry-updated", handler);
      window.removeEventListener("visitor-storage-updated", handler);
    };
  }, [refreshEvents]);

  useEffect(() => {
    const safeFocusRefresh = () => {
      const now = Date.now();
      if (now - lastFocusRefreshRef.current < 10000) return;
      lastFocusRefreshRef.current = now;
      refreshEvents();
    };
    const onFocus = () => safeFocusRefresh();
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        safeFocusRefresh();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refreshEvents]);

  const weekDays = useMemo(
    () => Array.from({ length: 5 }, (_, idx) => weekStart.add(idx, "day")),
    [weekStart]
  );

  const todayKey = formatDateKey(today);
  const todayEvents = eventsByDate[todayKey] || [];
  const visitedToday = todayEvents.filter((e) => e.type === "visit");
  const noticesToday = todayEvents.filter((e) => e.type !== "visit");

  const getEventChipProps = (event) => {
    if (event.type === "next-visit") return { label: "Upcoming", color: "info" };
    if (event.type === "summon-dispatch") return { label: "Dispatch", color: "warning" };
    if (event.type === "summon") return { label: "Appearance", color: "primary" };
    if (event.type === "visit") return { label: "Visit", color: "success" };
    return { label: event.type, color: "info" };
  };

  const renderEventList = (events, emptyMessage) => (
    <List dense disablePadding>
      {events.length ? (
        events.map((event, idx) => {
          const chipProps = getEventChipProps(event);
          return (
            <ListItem key={`${event.type}-${idx}`} alignItems="flex-start" disableGutters>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip size="small" label={chipProps.label} color={chipProps.color} />
                    <Typography variant="body2" component="span">
                      {event.title}
                    </Typography>
                  </Stack>
                }
                secondary={
                  <>
                    {event.enquiryCaseNum && (
                      <Typography variant="caption" display="block">
                        Enquiry: {event.enquiryCaseNum}
                      </Typography>
                    )}
                    {event.role && (
                      <Typography variant="caption" display="block">
                        Role: {event.role}
                      </Typography>
                    )}
                    {event.note && (
                      <Typography variant="caption" display="block">
                        Note: {event.note}
                      </Typography>
                    )}
                  </>
                }
              />
            </ListItem>
          );
        })
      ) : (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      )}
    </List>
  );

  const renderMissingLogs = () => (
    <List dense disablePadding>
      {missingLogs.length ? (
        missingLogs.map((item, idx) => (
          <ListItem key={`${item.enquirycasenum}-${item.cname}-${idx}`} disableGutters>
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip size="small" label="Missing log" color="warning" />
                  <Typography variant="body2">
                    {item.cname} — {dayLabel(item.appearance_date)}
                  </Typography>
                </Stack>
              }
              secondary={
                <>
                  <Typography variant="caption" display="block">
                    Enquiry: {item.enquirycasenum}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Notice: {item.notice_type} • Role: {item.roleofalleged || "Alleged"}
                  </Typography>
                </>
              }
            />
          </ListItem>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          {visitorMode ? "Audit checks are available after login." : "No missing logs detected."}
        </Typography>
      )}
    </List>
  );

  const renderConflicts = () => (
    <List dense disablePadding>
      {conflicts.length ? (
        conflicts.map((item, idx) => (
          <ListItem key={`${item.event_date}-${idx}`} disableGutters>
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label="Conflict" color="error" />
                  <Typography variant="body2">
                    {dayLabel(item.event_date)}
                  </Typography>
                </Stack>
              }
              secondary={
                <Typography variant="caption" display="block">
                  Total scheduled: {item.total}
                </Typography>
              }
            />
          </ListItem>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          {visitorMode ? "Conflict checks are available after login." : "No conflicts detected."}
        </Typography>
      )}
    </List>
  );



  if (isCompact) {
    return (
      <Box sx={{ width: "100%" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardHeader title="Month View" />
            <Divider />
            <CardContent sx={{ pt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar
                  value={calendarDate}
                  onChange={(value) => {
                    if (!value) return;
                    setCalendarDate(value);
                  }}
                  renderDay={(day, _selectedDates, pickersDayProps) => {
                    const key = formatDateKey(day);
                    const hasEvents = !!eventsByDate[key]?.length;
                    return (
                      <Badge
                        key={key}
                        overlap="circular"
                        badgeContent={hasEvents ? "•" : undefined}
                        color="primary"
                      >
                        <PickersDay {...pickersDayProps} />
                      </Badge>
                    );
                  }}
                />
              </LocalizationProvider>
            </CardContent>
          </Card>

          <Stack spacing={2} sx={{ flex: 1 }}>
            <Card variant="outlined">
              <CardHeader title="Visited Today" subheader={dayLabel(today)} />
              <Divider />
              <CardContent sx={{ maxHeight: 260, overflowY: "auto" }}>
                {renderEventList(visitedToday, "No visits logged today.")}
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardHeader title="Today's Notices" subheader="Accused / Complainant" />
              <Divider />
              <CardContent sx={{ maxHeight: 260, overflowY: "auto" }}>
                {renderEventList(noticesToday, "No notices scheduled for today.")}
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardHeader
                title="Audit"
                subheader={visitorMode ? "Login required for server audit checks" : "Missing Activity Logs"}
                action={
                  <Chip
                    size="small"
                    color={missingLogs.length ? "warning" : "success"}
                    label={missingLogs.length ? `${missingLogs.length} missing` : "All logged"}
                  />
                }
              />
              <Divider />
              <CardContent sx={{ maxHeight: 260, overflowY: "auto" }}>
                {renderMissingLogs()}
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardHeader
                title="Conflicts"
                subheader="High volume days"
                action={
                  <Chip
                    size="small"
                    color={conflicts.length ? "error" : "success"}
                    label={conflicts.length ? `${conflicts.length} days` : "Clear"}
                  />
                }
              />
              <Divider />
              <CardContent sx={{ maxHeight: 260, overflowY: "auto" }}>
                {renderConflicts()}
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardHeader
                title="Debug"
                subheader="Raw event counts"
                action={
                  <Chip
                    size="small"
                    label={showDebug ? "Hide" : "Show"}
                    onClick={() => setShowDebug((prev) => !prev)}
                    variant="outlined"
                  />
                }
              />
              <Divider />
              <CardContent sx={{ maxHeight: 200, overflowY: "auto" }}>
                {showDebug ? (
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      Total dates with events: {Object.keys(eventsByDate).length}
                    </Typography>
                    <Typography variant="body2">
                      Visits today: {visitedToday.length}
                    </Typography>
                    <Typography variant="body2">
                      Notices today: {noticesToday.length}
                    </Typography>
                    <Typography variant="body2">
                      Missing logs: {missingLogs.length}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Tap “Show” to view debug counts.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: "100%",
        p: 2,
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        gap: 2,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h5">
            Week of {dayLabel(weekStart)}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setWeekStart((prev) => prev.subtract(7, "day"))}
            aria-label="Previous week"
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setWeekStart((prev) => prev.add(7, "day"))}
            aria-label="Next week"
          >
            <ChevronRightIcon />
          </IconButton>
          <Chip
            label="This Week"
            variant="outlined"
            onClick={() => setWeekStart(getWeekStart(today))}
            sx={{ ml: 1 }}
          />
          {visitorMode && (
            <Button
              size="small"
              color="warning"
              variant="outlined"
              onClick={() => clearVisitorData(VISITOR_STORAGE_KEYS.visitLogs)}
            >
              Clear Local Data
            </Button>
          )}
        </Stack>

        <Grid container spacing={2}>
          {weekDays.map((day) => {
            const key = formatDateKey(day);
            const dayEvents = eventsByDate[key] || [];
            const visitEvents = dayEvents.filter((event) => event.type === "visit");
            const upcomingEvents = dayEvents.filter((event) => event.type !== "visit");
            return (
              <Grid item xs={12} sm={6} lg={4} key={key}>
                <Card
                  variant="outlined"
                  sx={{
                    height: 360,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardHeader
                    title={weekdayLabel(day)}
                    subheader={dayLabel(day)}
                    action={
                      <Stack direction="row" spacing={1}>
                        <Chip size="small" label={`${visitEvents.length} visited`} />
                        <Chip size="small" label={`${upcomingEvents.length} upcoming`} />
                      </Stack>
                    }
                  />
                  <Divider />
                  <CardContent sx={{ flex: 1, overflowY: "auto" }}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Visited
                        </Typography>
                        {renderEventList(visitEvents, "No visits logged.")}
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Upcoming
                        </Typography>
                        {renderEventList(upcomingEvents, "No upcoming visitors.")}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardHeader
            title="Audit"
            subheader={visitorMode ? "Login required for server audit checks" : "Missing Activity Logs"}
            action={
              <Chip
                size="small"
                color={missingLogs.length ? "warning" : "success"}
                label={missingLogs.length ? `${missingLogs.length} missing` : "All logged"}
              />
            }
          />
          <Divider />
          <CardContent sx={{ maxHeight: 260, overflowY: "auto" }}>
            {renderMissingLogs()}
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardHeader
            title="Conflicts"
            subheader="High volume days"
            action={
              <Chip
                size="small"
                color={conflicts.length ? "error" : "success"}
                label={conflicts.length ? `${conflicts.length} days` : "Clear"}
              />
            }
          />
          <Divider />
          <CardContent sx={{ maxHeight: 220, overflowY: "auto" }}>
            {renderConflicts()}
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardHeader
            title="Debug"
            subheader="Raw event counts"
            action={
              <Chip
                size="small"
                label={showDebug ? "Hide" : "Show"}
                onClick={() => setShowDebug((prev) => !prev)}
                variant="outlined"
              />
            }
          />
          <Divider />
          <CardContent sx={{ maxHeight: 200, overflowY: "auto" }}>
            {showDebug ? (
              <Stack spacing={1}>
                <Typography variant="body2">
                  Total dates with events: {Object.keys(eventsByDate).length}
                </Typography>
                <Typography variant="body2">
                  Visits this week: {weekDays.reduce((acc, day) => acc + (eventsByDate[formatDateKey(day)] || []).filter((e) => e.type === "visit").length, 0)}
                </Typography>
                <Typography variant="body2">
                  Notices this week: {weekDays.reduce((acc, day) => acc + (eventsByDate[formatDateKey(day)] || []).filter((e) => e.type !== "visit").length, 0)}
                </Typography>
                <Typography variant="body2">
                  Missing logs: {missingLogs.length}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Tap “Show” to view debug counts.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ width: { xs: "100%", lg: 360 }, flexShrink: 0 }}>
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardHeader title="Month View" />
            <Divider />
            <CardContent sx={{ pt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar
                  value={calendarDate}
                  onChange={(value) => {
                    if (!value) return;
                    setCalendarDate(value);
                    setWeekStart(getWeekStart(value));
                  }}
                  renderDay={(day, _selectedDates, pickersDayProps) => {
                    const key = formatDateKey(day);
                    const hasEvents = !!eventsByDate[key]?.length;
                    return (
                      <Badge
                        key={key}
                        overlap="circular"
                        badgeContent={hasEvents ? "•" : undefined}
                        color="primary"
                      >
                        <PickersDay {...pickersDayProps} />
                      </Badge>
                    );
                  }}
                />
              </LocalizationProvider>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardHeader title="Visited Today" subheader={dayLabel(today)} />
            <Divider />
            <CardContent sx={{ maxHeight: 260, overflowY: "auto" }}>
              {renderEventList(visitedToday, "No visits logged today.")}
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardHeader title="Today's Notices" subheader="Accused / Complainant" />
            <Divider />
            <CardContent sx={{ maxHeight: 260, overflowY: "auto" }}>
              {renderEventList(noticesToday, "No notices scheduled for today.")}
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </Box>
  );
}

